import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import OrderCard from './components/OrderCard';
import OrdersListSkeleton from './skeletons/OrdersListSkeleton';
import EmptyOrdersState from './EmptyOrdersState';

const Container = styled.div`
  padding: 24px 16px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: #f3f4f6;
  color: #479de6;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 6px;
  margin-bottom: 24px;
  text-decoration: none;
  border: 1px solid #e5e7eb;

  &:hover {
    background-color: #e5e7eb;
    color: #3a8ed4;
    text-decoration: none;
  }
`;

const Header = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`;

const PageButton = styled.button`
  background-color: ${(props) => (props.disabled ? '#e5e7eb' : '#479de6')};
  color: ${(props) => (props.disabled ? '#9ca3af' : '#fff')};
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #3a8ed4;
  }
`;

const PageInfo = styled.span`
  font-size: 14px;
  color: #666;
`;

const MyOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
  });

  const fetchOrders = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `/api/v2/parent/orders?page=${page}&per_page=10`,
      );
      setOrders(response.data.orders);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(pagination.current_page);
  }, []);

  const handlePrevPage = () => {
    if (pagination.current_page > 1) {
      fetchOrders(pagination.current_page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.current_page < pagination.total_pages) {
      fetchOrders(pagination.current_page + 1);
    }
  };

  return (
    <div className="bnl-dashboard-container">
      <div className="bnl-dashboard">
        <Container>
          <BackLink to="/my-account">&larr; Back to Account</BackLink>

          <Header>My Orders</Header>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {loading ? (
            <OrdersListSkeleton />
          ) : orders.length === 0 ? (
            <EmptyOrdersState />
          ) : (
            <>
              {orders.map((order) => (
                <OrderCard key={order.order_id} order={order} />
              ))}

              {pagination.total_pages > 1 && (
                <PaginationWrapper>
                  <PageButton
                    onClick={handlePrevPage}
                    disabled={pagination.current_page <= 1}
                  >
                    Previous
                  </PageButton>

                  <PageInfo>
                    Page {pagination.current_page} of {pagination.total_pages} (
                    {pagination.total_count} orders)
                  </PageInfo>

                  <PageButton
                    onClick={handleNextPage}
                    disabled={pagination.current_page >= pagination.total_pages}
                  >
                    Next
                  </PageButton>
                </PaginationWrapper>
              )}
            </>
          )}
        </Container>
      </div>
    </div>
  );
};

export default MyOrdersPage;
